import { Cat, KINDS } from '../gameplay/registry';
import { C } from '../gameplay/types';
import { FX_ONE } from '../engine/simulation/fixed';
import { playerScore } from '../engine/simulation/systems/victory';
import type { World } from '../engine/simulation/world';

/** One player's figures at a moment in the match. */
export interface PlayerSample {
  /** Resources gathered so far, per resource (food, wood, gold, stone). */
  gathered: number[];
  score: { economy: number; military: number; technology: number; buildings: number; total: number };
  pop: number;
  villagers: number;
  military: number;
  buildings: number;
  kills: number;
  losses: number;
  age: number;
  techs: number;
  defeated: boolean;
}

export interface Sample {
  /** Game seconds. */
  t: number;
  players: PlayerSample[];
}

/** Game ticks between samples (30 game seconds). */
export const SAMPLE_TICKS = 600;

export function samplePlayers(w: World): PlayerSample[] {
  const n = w.players.length;
  const villagers = new Array(n).fill(0);
  const military = new Array(n).fill(0);
  const buildings = new Array(n).fill(0);
  for (let s = 0; s < w.highSlot; s++) {
    if (!w.alive[s]) continue;
    const o = w.owner[s];
    if (o < 0) continue;
    if (w.cat[s] === Cat.Unit) {
      const cl = KINDS[w.kind[s]].unit!.classes;
      if (cl & C.Villager) villagers[o]++;
      else if (cl & C.Military) military[o]++;
    } else if (w.cat[s] === Cat.Building && w.built[s]) buildings[o]++;
  }
  return w.players.map((p) => {
    const sc = playerScore(w, p.id);
    return {
      gathered: p.gathered.map((g) => Math.trunc(g / FX_ONE)),
      score: { economy: sc.economy, military: sc.military, technology: sc.technology, buildings: sc.buildings, total: sc.total },
      pop: p.pop,
      villagers: villagers[p.id],
      military: military[p.id],
      buildings: buildings[p.id],
      kills: p.kills,
      losses: p.losses,
      age: p.age,
      techs: p.researched.reduce((a, b) => a + b, 0),
      defeated: p.defeated,
    };
  });
}

/**
 * The match as it went: a sample of every player's figures every 30 game seconds, plus the moment
 * each player reached each age. Kept by the client for the results screen; it does not touch the
 * simulation, so it costs nothing in lockstep. A match joined part way (a loaded save) starts from
 * the moment it was loaded.
 */
export class MatchHistory {
  samples: Sample[] = [];
  /** Game seconds at which each player reached each age (index = age). */
  ageTimes: number[][] = [];

  record(w: World): void {
    const t = w.tick / 20;
    const last = this.samples[this.samples.length - 1];
    if (last && last.t === t) return;
    this.samples.push({ t, players: samplePlayers(w) });
  }

  /** Called every tick; samples on the interval and always at the end. */
  update(w: World): void {
    if (!this.ageTimes.length) this.ageTimes = w.players.map((p) => (p.age === 0 ? [0] : []));
    if (w.tick % SAMPLE_TICKS === 0 || w.gameOver || !this.samples.length) this.record(w);
  }

  ageReached(owner: number, age: number, w: World): void {
    if (!this.ageTimes[owner]) return;
    this.ageTimes[owner][age] = w.tick / 20;
  }
}

/** How a victory was won, in words for the end screen. */
export function victoryDetail(reason: string, cfg: { wonderSeconds: number; territorySeconds: number; economicTarget: number; timeLimitSeconds: number }): string {
  const mins = (s: number) => `${Math.round(s / 60)} minute${Math.round(s / 60) === 1 ? '' : 's'}`;
  switch (reason) {
    case 'Conquest': return 'outlasted every other player';
    case 'Wonder': return `kept a Wonder standing for ${mins(cfg.wonderSeconds)}`;
    case 'Territorial': return `held every strategic point for ${mins(cfg.territorySeconds)}`;
    case 'Economic': return `gathered ${cfg.economicTarget.toLocaleString('en')} resources`;
    case 'Last human out': return 'had the highest score when the last human player left the match';
    case 'Time limit (score)': return `had the highest score when the ${mins(cfg.timeLimitSeconds)} ran out`;
    default: return reason;
  }
}
